import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Layout, Menu, Badge, Dropdown, Space, Typography, Avatar, Popover, List, Button, Select, Tooltip } from 'antd';
import {
  DashboardOutlined, SendOutlined, TeamOutlined, TruckOutlined,
  HistoryOutlined, BellOutlined, SettingOutlined, LogoutOutlined,
  UserOutlined, ToolOutlined, EnvironmentOutlined,
  MonitorOutlined, InboxOutlined, MenuFoldOutlined, MenuUnfoldOutlined,
  BankOutlined, IdcardOutlined, BulbOutlined, BugOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useBranch } from '../hooks/BranchContext';
import { useWSNotification } from '../hooks/useWebSocket';
import TodayResetContext from '../hooks/TodayResetContext';
import api from '../api/client';
import PwaInstallBanner from './PwaInstallBanner';
import FeedbackModal from './FeedbackModal';

const { Header, Sider, Content } = Layout;

/** Ant Design breakpoint `lg` — ниже сайдбар сворачивается в 0. */
const SIDER_BREAKPOINT_PX = 992;

const managerMenu = [
  { key: '/dashboard', icon: <DashboardOutlined />, label: 'Доска' },
  { key: '/axiom-review', icon: <InboxOutlined />, label: 'Из Axiom' },
  { key: '/onec-review', icon: <InboxOutlined />, label: 'Из 1С' },
  { key: '/clients', icon: <TeamOutlined />, label: 'Контрагенты' },
  { key: '/transport', icon: <TruckOutlined />, label: 'ТК' },
  { key: '/legal-entities', icon: <IdcardOutlined />, label: 'Наши юрлица' },
  { key: '/history', icon: <HistoryOutlined />, label: 'История' },
];

const expeditorMenu = [
  { key: '/expeditor', icon: <SendOutlined />, label: 'Сегодня' },
];

const adminMenu = [
  ...managerMenu,
  { type: 'divider' },
  { key: '/admin/users', icon: <UserOutlined />, label: 'Пользователи' },
  { key: '/admin/branches', icon: <BankOutlined />, label: 'Филиалы' },
  { key: '/admin/parser', icon: <ToolOutlined />, label: 'Парсер Axiom' },
  { key: '/admin/parser-1c', icon: <ToolOutlined />, label: 'Парсинг 1С' },
  { key: '/admin/cities', icon: <EnvironmentOutlined />, label: 'Города' },
  { key: '/admin/system', icon: <MonitorOutlined />, label: 'Система' },
];

const roleLabels = { admin: 'Админ', manager: 'Менеджер', expeditor: 'Экспедитор' };

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { currentBranch, branches, canSwitch, setSelectedBranchId, selectedBranchId } = useBranch();
  const navigate = useNavigate();
  const location = useLocation();
  const branchTitle = currentBranch?.name
    ? `Логист · ${currentBranch.name}`
    : 'Логист';
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [bellColor, setBellColor] = useState(undefined);
  const [popoverOpen, setPopoverOpen] = useState(false);
  // Обратная связь: модалка предложения/ошибки + пункт меню для разработчика
  const [feedback, setFeedback] = useState(null); // 'suggestion' | 'error'
  const [isDeveloper, setIsDeveloper] = useState(false);
  useEffect(() => {
    // Фронт не угадывает, кто разработчик — спрашивает API. 403 = обычный пользователь.
    api.get('/feedback/dev').then(() => setIsDeveloper(true)).catch(() => {});
  }, []);
  /** Свёрнут ли сайдбар (на мобилке — да). Плавающий zero-width trigger отключён. */
  const [collapsed, setCollapsed] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < SIDER_BREAKPOINT_PX,
  );
  const [siderBroken, setSiderBroken] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < SIDER_BREAKPOINT_PX,
  );

  const fetchUnread = useCallback(async () => {
    try {
      const resp = await api.get('/notifications/count');
      setUnreadCount(resp.data?.count || 0);
    } catch (e) { /* silent */ }
  }, []);

  const updateBellColor = useCallback((items) => {
    const unread = items.filter(i => !i.is_read);
    if (unread.some(i => i.type === 'problem' || i.notification_type === 'problem')) setBellColor('#ff4d4f');
    else if (unread.some(i => i.type === 'completed' || i.notification_type === 'completed')) setBellColor('#52c41a');
    else setBellColor(undefined);
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const resp = await api.get('/notifications?limit=10');
      setNotifications(resp.data || []);
      updateBellColor(resp.data || []);
    } catch (e) { /* silent */ }
  }, [updateBellColor]);

  useEffect(() => { fetchUnread(); fetchNotifications(); }, [fetchUnread, fetchNotifications]);

  const handleWSNotification = useCallback((data) => {
    setUnreadCount(prev => prev + 1);
    if (data.title) {
      setNotifications(prev => {
        const next = [data, ...prev].slice(0, 10);
        updateBellColor(next);
        return next;
      });
      if (data.notification_type === 'problem') setBellColor('#ff4d4f');
      else if (data.notification_type === 'completed' && bellColor !== '#ff4d4f') setBellColor('#52c41a');
    }
  }, [updateBellColor, bellColor]);

  useWSNotification(handleWSNotification);

  const markAllRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setUnreadCount(0);
      setBellColor(undefined);
      fetchNotifications();
    } catch (e) { /* silent */ }
  };

  const markOneRead = async (notif) => {
    if (notif.is_read) return;
    try {
      await api.post(`/notifications/${notif.id}/read`);
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => {
        const next = prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n);
        updateBellColor(next);
        return next;
      });
    } catch (e) { /* silent */ }
  };

  const isExpeditor = user?.role === 'expeditor';
  const menuItems = user?.role === 'expeditor' ? expeditorMenu
    : user?.role === 'admin' ? adminMenu : managerMenu;
  const menuWithDev = isDeveloper
    ? [...menuItems, { type: 'divider' }, { key: '/admin/feedback', icon: <BulbOutlined />, label: 'Обратная связь' }]
    : menuItems;

  const resetDateRef = useRef(null);
  const resetToToday = useCallback(() => {
    if (resetDateRef.current) resetDateRef.current();
  }, []);
  const registerReset = useCallback((fn) => {
    resetDateRef.current = fn;
  }, []);

  const pathParts = location.pathname.split('/').filter(Boolean);
  // /transport/123 → подсветка пункта «ТК», не «сырой» путь
  let selectedKey = '/' + pathParts.join('/');
  if (pathParts[0] === 'transport') selectedKey = '/transport';
  else if (pathParts[0] === 'clients') selectedKey = '/clients';
  const isDashboard = location.pathname === '/dashboard' || location.pathname === '/';
  const contentStyle = isDashboard
    ? {
        margin: 8,
        padding: 12,
        background: '#fff',
        borderRadius: 8,
        minHeight: 'calc(100vh - 80px)',
        overflow: 'auto',
      }
    : {
        margin: 16,
        padding: 24,
        background: '#fff',
        borderRadius: 8,
        minHeight: 280,
        overflow: 'auto',
      };

  const dropdownItems = {
    items: [
      { key: 'profile', label: `${user?.full_name} (${roleLabels[user?.role]})`, disabled: true },
      { type: 'divider' },
      { key: 'logout', label: 'Выйти', icon: <LogoutOutlined />, danger: true },
    ],
    onClick: ({ key }) => {
      if (key === 'logout') logout();
    },
  };

  if (isExpeditor) {
    // На широком экране — узкая колонка как на телефоне (~480px), по центру
    const expeditorCol = {
      width: '100%',
      maxWidth: 480,
      margin: '0 auto',
    };
    return (
      <>
      <TodayResetContext.Provider value={{ registerReset, resetToToday }}>
      <Layout style={{ minHeight: '100vh', fontSize: 16, background: '#f5f5f5' }}>
        <Content style={{
          ...expeditorCol,
          padding: '0 12px',
          paddingBottom: 'calc(72px + env(safe-area-inset-bottom))',
          background: '#f5f5f5',
        }}>
          <div style={{
            padding: '10px 0 4px',
            textAlign: 'center',
            fontWeight: 600,
            fontSize: 15,
          }}>
            {branchTitle}
          </div>
          <PwaInstallBanner />
          {children}
          <div style={{ padding: '8px 0 2px', display: 'flex', justifyContent: 'center', gap: 24 }}>
            <span onClick={() => setFeedback('suggestion')} style={{ cursor: 'pointer', color: '#faad14', fontSize: 13 }}>
              <BulbOutlined /> Предложение
            </span>
            <span onClick={() => setFeedback('error')} style={{ cursor: 'pointer', color: '#999', fontSize: 13 }}>
              <BugOutlined /> Ошибка
            </span>
          </div>
        </Content>
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: '#fff', borderTop: '1px solid #f0f0f0',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}>
          <div style={{
            ...expeditorCol,
            height: 56,
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
          }}>
            <div onClick={resetToToday} style={{ textAlign: 'center', cursor: 'pointer', color: selectedKey === '/expeditor' ? '#1890ff' : '#999' }}>
              <SendOutlined style={{ fontSize: 20 }} />
              <div style={{ fontSize: 11 }}>Сегодня</div>
            </div>
            <div onClick={logout} style={{ textAlign: 'center', cursor: 'pointer', color: '#999' }}>
              <LogoutOutlined style={{ fontSize: 20 }} />
              <div style={{ fontSize: 11 }}>Выйти</div>
            </div>
          </div>
        </div>
      </Layout>
      </TodayResetContext.Provider>
      <FeedbackModal open={!!feedback} type={feedback || 'suggestion'} onClose={() => setFeedback(null)} />
      </>
    );
  }

  return (
    <TodayResetContext.Provider value={{ registerReset, resetToToday }}>
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        width={220}
        theme="light"
        breakpoint="lg"
        collapsedWidth={0}
        collapsed={collapsed}
        collapsible
        trigger={null}
        onBreakpoint={(broken) => {
          setSiderBroken(broken);
          setCollapsed(broken);
        }}
        onCollapse={(value) => setCollapsed(value)}
      >
        <div style={{
          height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderBottom: '1px solid #f0f0f0', padding: '0 8px', textAlign: 'center',
        }}>
          <Typography.Text strong style={{ fontSize: currentBranch?.name ? 15 : 18, lineHeight: 1.2 }}>
            {branchTitle}
          </Typography.Text>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuWithDev}
          onClick={({ key }) => {
            navigate(key);
            if (siderBroken) setCollapsed(true);
          }}
          style={{ borderRight: 0 }}
        />
      </Sider>
      <Layout>
        <Header style={{
          background: '#fff', padding: siderBroken ? '0 12px' : '0 24px', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
          borderBottom: '1px solid #f0f0f0', height: 64,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 40, flex: 1 }}>
            {siderBroken && (
              <Button
                type="text"
                aria-label="Меню"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed((c) => !c)}
                style={{ fontSize: 18, width: 40, height: 40 }}
              />
            )}
            {siderBroken && (
              <Typography.Text strong style={{ whiteSpace: 'nowrap' }}>{branchTitle}</Typography.Text>
            )}
            {canSwitch && (
              <Select
                size="small"
                style={{ minWidth: 140, maxWidth: 200 }}
                value={selectedBranchId ?? undefined}
                options={branches.map((b) => ({ value: b.id, label: b.name }))}
                onChange={(id) => {
                  setSelectedBranchId(id);
                  window.location.reload();
                }}
                placeholder="Филиал"
              />
            )}
          </div>
          <Space size="middle">
            <Tooltip title="Внести предложение">
              <Button type="text" icon={<BulbOutlined />} aria-label="Внести предложение"
                onClick={() => setFeedback('suggestion')} style={{ fontSize: 16 }} />
            </Tooltip>
            <Tooltip title="Сообщить об ошибке">
              <Button type="text" icon={<BugOutlined />} aria-label="Сообщить об ошибке"
                onClick={() => setFeedback('error')} style={{ fontSize: 16 }} />
            </Tooltip>
            <Popover
              open={popoverOpen}
              onOpenChange={(open) => { setPopoverOpen(open); if (open) fetchNotifications(); }}
              title={
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <span>Уведомления</span>
                  {unreadCount > 0 && (
                    <Button type="link" size="small" onClick={markAllRead}>Прочитать все</Button>
                  )}
                </Space>
              }
              content={
                <List
                  size="small"
                  dataSource={notifications}
                  style={{ width: 320, maxHeight: 400, overflow: 'auto' }}
                  locale={{ emptyText: 'Нет уведомлений' }}
                  renderItem={item => (
                    <List.Item
                      onClick={() => {
                        markOneRead(item);
                        if (item.task_id) navigate('/tasks/' + item.task_id);
                        setPopoverOpen(false);
                      }}
                      style={{ opacity: item.is_read ? 0.5 : 1, cursor: 'pointer' }}
                    >
                      <List.Item.Meta
                        title={<Typography.Text style={{ fontSize: 13 }}>{item.title}</Typography.Text>}
                        description={
                          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                            {item.message || ''} {item.created_at ? new Date(item.created_at).toLocaleString('ru-RU') : ''}
                          </Typography.Text>
                        }
                      />
                    </List.Item>
                  )}
                />
              }
              trigger="click"
            >
              <Badge dot={bellColor === '#ff4d4f'} count={bellColor === '#ff4d4f' ? 0 : unreadCount} size="small">
                <BellOutlined style={{
                  fontSize: 18,
                  cursor: 'pointer',
                  color: bellColor,
                  animation: bellColor === '#ff4d4f' ? 'bellShake 3s ease-in-out infinite' : 'none',
                  transformOrigin: 'top center',
                }} />
              </Badge>
              <style>{`
                @keyframes bellShake {
                  0%, 16.6%, 100% { transform: rotate(0deg); }
                  3.3% { transform: rotate(12deg); }
                  6.6% { transform: rotate(-10deg); }
                  10% { transform: rotate(8deg); }
                  13.3% { transform: rotate(-6deg); }
                }
              `}</style>
            </Popover>
            <Dropdown menu={dropdownItems} trigger={['click']}>
              <Space style={{ cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} />
                <span>{user?.full_name}</span>
              </Space>
            </Dropdown>
          </Space>
        </Header>
        <Content style={contentStyle}>
          {children}
        </Content>
      </Layout>
    </Layout>
    <FeedbackModal open={!!feedback} type={feedback || 'suggestion'} onClose={() => setFeedback(null)} />
    </TodayResetContext.Provider>
  );
}
